import AppConstants from "@/constants/AppConstants";
import {
  OPACITY,
  BLUR,
  SHADOW_STYLE,
  SHADOW_COLOR,
  SHADOW_OFFSET_X,
  SHADOW_OFFSET_Y,
  SHADOW_BLUR,
  BORDER_RADIUS,
} from "@/constants/MergeConstants";
import { BASE_INPUT_CONFIG } from "./BaseInputConfigMap";
import { createPropertyMapper } from "./BasePropertyMapper";

const {
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_LOTTIE,
  BLEND_MODE_NORMAL,
} = AppConstants;

export const DEFAULT_EFFECTS_VALUES = {
  [OPACITY]: 100,
  [BLUR]: 0,
  [SHADOW_STYLE]: "none",
  [SHADOW_COLOR]: "colors-0:50",
  [SHADOW_OFFSET_X]: 2,
  [SHADOW_OFFSET_Y]: 2,
  [SHADOW_BLUR]: 6,
  [BORDER_RADIUS]: 0,
};

export const EFFECTS_PROPERTY_META_MAP = {
  [OPACITY]: {
    ...BASE_INPUT_CONFIG[OPACITY],
  },
  [BLUR]: {
    ...BASE_INPUT_CONFIG[BLUR],
  },
  [SHADOW_STYLE]: {
    ...BASE_INPUT_CONFIG[SHADOW_STYLE],
  },
  [SHADOW_COLOR]: {
    ...BASE_INPUT_CONFIG[SHADOW_COLOR],
  },
  [SHADOW_OFFSET_X]: {
    ...BASE_INPUT_CONFIG[SHADOW_OFFSET_X],
  },
  [SHADOW_OFFSET_Y]: {
    ...BASE_INPUT_CONFIG[SHADOW_OFFSET_Y],
  },
  [SHADOW_BLUR]: {
    ...BASE_INPUT_CONFIG[SHADOW_BLUR],
  },
  [BORDER_RADIUS]: {
    ...BASE_INPUT_CONFIG[BORDER_RADIUS],
  },
};

export const DROP_SHADOW_ELEMENTS = [
  ELEMENT_TYPE_LINE,
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_LOTTIE,
];

export const INSET_SHADOW_ELEMENTS = [ELEMENT_TYPE_SIMPLE, ELEMENT_TYPE_GRAPHIC];

export const BORDER_RADIUS_ELEMENTS = [
  ELEMENT_TYPE_GRAPHIC,
  ELEMENT_TYPE_ICON,
  ELEMENT_TYPE_TEXT,
  ELEMENT_TYPE_HTML_TEXT,
  ELEMENT_TYPE_SIMPLE,
  ELEMENT_TYPE_SVG,
  ELEMENT_TYPE_VIDEO,
  ELEMENT_TYPE_GALLERY,
  ELEMENT_TYPE_IFRAME,
  ELEMENT_TYPE_LOTTIE,
];

export const getEffectsForElementType = (elementType) => {
  const effects = [OPACITY, BLUR];

  if (DROP_SHADOW_ELEMENTS.includes(elementType)) {
    effects.push(SHADOW_STYLE);
  }

  if (BORDER_RADIUS_ELEMENTS.includes(elementType)) {
    effects.push(BORDER_RADIUS);
  }

  return effects;
};

export const supportsInsetShadow = (elementType) => {
  return INSET_SHADOW_ELEMENTS.includes(elementType);
};

/**
 * Factory function to create an effects property mapper for a given element type.
 * Reduces duplication across element-specific mapper files.
 *
 * @param {string} elementType - The element type constant (e.g., ELEMENT_TYPE_SIMPLE)
 * @returns {Function} A property mapper configured for the specified element type
 *
 * @example
 * export const SimplePropertyMapper = createEffectsPropertyMapper(ELEMENT_TYPE_SIMPLE);
 */
export const createEffectsPropertyMapper = (elementType) =>
  createPropertyMapper({
    propertyMetaMap: EFFECTS_PROPERTY_META_MAP,
    customizableEffects: getEffectsForElementType(elementType),
    defaultValues: DEFAULT_EFFECTS_VALUES,
  });

// Export individual mappers for backward compatibility and testing
export const SimplePropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_SIMPLE);
export const LinePropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_LINE);
export const TextPropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_TEXT);
export const IconPropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_ICON);
export const GraphicPropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_GRAPHIC);
export const SvgPropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_SVG);
export const VideoPropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_VIDEO);
export const GalleryPropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_GALLERY);
export const EmbedPropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_IFRAME);
export const LottiePropertyMapper = createEffectsPropertyMapper(AppConstants.ELEMENT_TYPE_LOTTIE);
